import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { Box } from '@tanium/react-box';
import { useTranslation } from 'react-i18next';
import { useHistory } from '@tanium/react-history-context';
import Helper from 'utils/Helper';
import Page from 'components/base_page/Page';
import Spinner from 'components/common/spinner/Spinner';
import NotificationInfoDisplay from 'components/common/notificationInfo/NotificationInfoDisplay';
import TbacDetails from 'components/common/accessInfo/TbacDisplay';
import { MsEntraID } from 'gql/MsEntraID';
import MsEntraIDSelectors from 'components/pages/console/store/MsEntraIDSelectors';
import MsEntraIDSlice from 'components/pages/console/store/MsEntraIDSlice';
import MsEntraIDGroupTargetAccessList from 'components/common/msEntraIDGroupTargetAccess/MsEntraIDGroupTargetAccessList';
import ConfirmationModal from 'components/pages/request/requestPartials/ConfirmationModal';
import { PermissionAction, PermissionResource, hasPermission } from 'gql/Permission';
import UserSelectors from 'components/app/store/UserSelectors';
import { CREATE_REQUEST_ROUTE, EDIT_MS_ENTRA_ID_ROUTE } from 'Route';
import {
  TARGET_ID,
  TARGET_TYPE,
} from 'components/pages/request/requestPartials/createRequestFieldsPartials/Environments';
import { RequestTargetType } from 'gql/Request';
import { Button } from '@tanium/react-button';
import RequestListPanel from 'components/common/requestListPanel/RequestListPanel';
import MsEntraIDDetails from './msEntraIDPartials/MsEntraIDDetails';

const MsEntraIDPage: React.VFC = () => {
  const { msEntraIDId } = useParams<{ msEntraIDId: string }>();
  const [confirmDeleteModal, setConfirmDeletetModal] = useState<boolean>(false);
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const user = useSelector(UserSelectors.selectUser);
  const msEntraID: MsEntraID | undefined = useSelector(MsEntraIDSelectors.selectMsEntraID(msEntraIDId));
  const canEdit = hasPermission(user?.permissions, PermissionAction.WRITE, PermissionResource.MS_ENTRA_ID);

  // Get any needed metadata for this page on load
  useEffect(() => {
    dispatch(MsEntraIDSlice.actions.getMsEntraIDbyID({ id: msEntraIDId }));
  }, [dispatch, msEntraIDId]);

  const buttons: JSX.Element[] = [];

  const canCreateRequest = hasPermission(user?.permissions, PermissionAction.WRITE, PermissionResource.REQUEST);
  if (canCreateRequest) {
    buttons.push(
      <Link
        to={`${CREATE_REQUEST_ROUTE.url}?${TARGET_TYPE}=${RequestTargetType.MsEntraID}&${TARGET_ID}=${msEntraIDId}`}
      >
        <Button variant="secondary" data-testid={'request-access'}>
          {t('REQUEST_ACCESS')}
        </Button>
      </Link>
    );
  }

  /* istanbul ignore next */
  const handleDelete = () => {
    /* istanbul ignore next */
    dispatch(MsEntraIDSlice.actions.deleteMsEntraID(msEntraIDId));
  };

  // Handle opening / closing the delete modal
  const openConfirmDeleteModal = () => {
    setConfirmDeletetModal(true);
  };

  const closeConfirmModal = (proceed: boolean) => {
    setConfirmDeletetModal(false);
    /* istanbul ignore next */
    if (proceed) {
      /* istanbul ignore next */
      handleDelete();
    }
  };

  if (canEdit) {
    buttons.push(
      <Button data-testid="delete-ms-entra-id" onClick={openConfirmDeleteModal}>
        {t('DELETE')}
      </Button>
    );
    buttons.push(
      <Button
        data-testid="edit-ms-entra-id"
        onClick={(): void => {
          history.push(Helper.interpolateRoute(EDIT_MS_ENTRA_ID_ROUTE.url, { msEntraIDId }));
        }}
      >
        {t('EDIT')}
      </Button>
    );
  }

  return (
    <Page
      title={msEntraIDId}
      breadcrumbs={[
        {
          href: '/?tab=msEntraID',
          label: t('MS_ENTRA_ID'),
        },
      ]}
      testId="ms-entra-id-page"
      buttons={buttons}
    >
      {msEntraID ? (
        <Box mb={2}>
          <Box pb={1}>
            <MsEntraIDDetails msEntraID={msEntraID} />
          </Box>
          <Box pb={1}>
            <TbacDetails baseTarget={msEntraID} targetAccessLookup={{}} />
          </Box>
          <Box pb={1}>
            <NotificationInfoDisplay
              approvalSlackChannel={msEntraID.approvalSlackChannel}
              notifyAutoApproval={msEntraID.notifyAutoApproval}
              enablePaging={msEntraID.enablePaging}
            />
          </Box>
          <Box pb={1}>
            <RequestListPanel targetId={msEntraIDId} />
          </Box>
          <Box pb={1}>
            <MsEntraIDGroupTargetAccessList
              targetOrGroupID={msEntraIDId}
              canEdit={hasPermission(
                user?.permissions,
                PermissionAction.WRITE,
                PermissionResource.MS_ENTRA_ID_GROUP_TARGET_ACCESS
              )}
              includeAllTargetGroups={true}
            />
          </Box>
          <ConfirmationModal
            headerText={`${t('DELETE_MS_ENTRA_ID')}?`}
            isOpen={confirmDeleteModal}
            timeoutSeconds={2}
            confirmationText={t('DELETE')}
            closeModal={closeConfirmModal}
          >
            <p>
              {t('DELETE_MS_ENTRA_ID_TEXT')}
              <strong>{msEntraIDId}</strong>
            </p>
          </ConfirmationModal>
        </Box>
      ) : (
        <Spinner data-testid="tanium-spinner" />
      )}
    </Page>
  );
};

export default MsEntraIDPage;
